import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Template from "../main/template";
import Header from "./Header";
import Footer from "./Footer";
import {
  buildHubSearchIndex,
  loadHubSearchIndex,
} from "../data/hubSearchIndex";
import { searchHub } from "../utils/hubSearch";

const styles = {
  wrap: {
    maxWidth: 1160,
    margin: "0 auto",
    padding: "24px 16px 48px",
  },
  hero: {
    background: "linear-gradient(135deg, #f6f0ff, #fff6e8)",
    border: "1px solid #eadcfb",
    borderRadius: 24,
    padding: "24px 22px",
    boxShadow: "0 14px 34px rgba(43, 22, 82, 0.08)",
    marginBottom: 18,
    textAlign: "left",
  },
  eyebrow: {
    display: "inline-block",
    padding: "6px 12px",
    borderRadius: 999,
    background: "rgba(95, 42, 184, 0.10)",
    color: "#5f2ab8",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 10,
  },
  title: {
    margin: 0,
    fontSize: "clamp(1.75rem, 1.2vw + 1.35rem, 2.4rem)",
    lineHeight: 1.08,
    color: "#21153a",
  },
  sub: {
    margin: "10px 0 0",
    color: "#5a5470",
    fontSize: "1rem",
    lineHeight: 1.65,
  },
  meta: {
    margin: "16px 0 0",
    color: "#5f2ab8",
    fontWeight: 700,
  },
  notice: {
    margin: "12px 0 0",
    color: "#7b5a18",
    fontSize: "0.9rem",
  },
  empty: {
    background: "#fff",
    border: "1px dashed #d9c9f1",
    borderRadius: 22,
    padding: "40px 22px",
    textAlign: "center",
    color: "#5c5470",
    boxShadow: "0 10px 26px rgba(43, 22, 82, 0.05)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 16,
    marginTop: 18,
  },
  card: {
    display: "block",
    textDecoration: "none",
    background: "#fff",
    border: "1px solid #eadcfb",
    borderRadius: 22,
    padding: 18,
    color: "#21153a",
    boxShadow: "0 10px 24px rgba(43, 22, 82, 0.06)",
  },
  type: {
    display: "inline-flex",
    padding: "5px 10px",
    borderRadius: 999,
    background: "#f1e9fd",
    color: "#5f2ab8",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: 10,
  },
  cardTitle: {
    margin: "0 0 8px",
    fontSize: "1.18rem",
    lineHeight: 1.25,
  },
  cardText: {
    margin: 0,
    color: "#5c5470",
    lineHeight: 1.65,
    fontSize: "0.98rem",
  },
};

export default function HubSearchResults() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const query = (params.get("q") || "").trim();
  const language =
    params.get("lang") ||
    localStorage.getItem("hubLanguage") ||
    "english";

  const [items, setItems] = useState(() =>
    buildHubSearchIndex(language)
  );
  const [isLoadingBusinesses, setIsLoadingBusinesses] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    // Show the static pages/categories immediately while businesses load.
    setItems(buildHubSearchIndex(language));
    setIsLoadingBusinesses(true);
    setLoadError("");

    loadHubSearchIndex(language, {
      signal: controller.signal,
    })
      .then((nextItems) => {
        setItems(nextItems);
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;

        console.error("Unable to load business search index.", error);
        setLoadError(
          "Business listings could not be loaded. Page, category, and community search is still available."
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoadingBusinesses(false);
        }
      });

    return () => controller.abort();
  }, [language]);

  const results = useMemo(
    () => searchHub(query, items),
    [query, items]
  );

  const handleHeaderSearch = () => {};

  const handleSearchSubmit = (nextQuery) => {
    const q = (nextQuery || "").trim();

    if (!q) {
      navigate("/hub");
      return;
    }

    navigate(
      `/hub/search?q=${encodeURIComponent(q)}&lang=${encodeURIComponent(
        language
      )}`
    );
  };

  return (
    <div className="App">
      <Template variant="hub">
        <div className="headerSection">
          <Header
            enableHeaderSearch
            onSearch={handleHeaderSearch}
            onSearchSubmit={handleSearchSubmit}
          />
        </div>

        <div className="menuSection" />

        <div className="mainSection">
          <div style={styles.wrap}>
            <section style={styles.hero}>
              <div style={styles.eyebrow}>Hub search</div>
              <h1 style={styles.title}>Search results</h1>
              <p style={styles.sub}>
                Search across businesses, Hub pages, business categories,
                services, and connected community routes.
              </p>

              <div style={styles.meta}>
                {!query
                  ? "Type something in the Hub search bar to begin."
                  : isLoadingBusinesses
                    ? `Loading business matches for “${query}”…`
                    : `${results.length} result${
                        results.length === 1 ? "" : "s"
                      } for “${query}”`}
              </div>

              {loadError && <p style={styles.notice}>{loadError}</p>}
            </section>

            {!query ? (
              <div style={styles.empty}>
                <h3>Start with a real keyword</h3>
                <p>Try business names, services, categories, or communities.</p>
              </div>
            ) : isLoadingBusinesses && results.length === 0 ? (
              <div style={styles.empty}>
                <h3>Loading businesses…</h3>
                
              </div>
            ) : results.length === 0 ? (
              <div style={styles.empty}>
                <h3>No strong matches</h3>
                <p>Try a broader term, a simpler keyword, or fewer words.</p>
              </div>
            ) : (
              <div style={styles.grid}>
                {results.map((result) => (
                  <Link
                    key={result.id}
                    to={result.url}
                    style={styles.card}
                  >
                    <span style={styles.type}>{result.type}</span>
                    <h3 style={styles.cardTitle}>{result.title}</h3>
                    <p style={styles.cardText}>{result.description}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="rightNavSection" />
        <Footer />
      </Template>
    </div>
  );
}
